<script lang="ts">
    import type * as Pdfjs from 'pdfjs-dist/types/src/pdf';
    import type {PdfjsModule} from '../types';
    import type {TextContent} from 'pdfjs-dist/types/src/display/api';
    import {getThemeContext} from 'shared/theme/portaro-theme';
    import {getViewedContentContext} from '../../../../lib/mv-viewed-content';
    import {onDestroy, onMount} from 'svelte';
    import {exists} from 'shared/utils/custom-utils';
    import {fade} from 'svelte/transition';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import MvPdfPageAnnotations from './MvPdfPageAnnotations.svelte';
    import MvPdfPageTextLayer from './MvPdfPageTextLayer.svelte';

    export let pdfDocument: Pdfjs.PDFDocumentProxy;
    export let page: Pdfjs.PDFPageProxy;
    export let pages: Pdfjs.PDFPageProxy[];
    export let module: PdfjsModule;
    export let downloadable: boolean;

    const currentTheme = getThemeContext().currentTheme;
    const viewedContent = getViewedContentContext();
    const renderingFadeAnimParams = {duration: 125};
    let zoom = 1;
    const zoomUnsubscribe = viewedContent.zoom.subscribe((currentZoom) => zoom = currentZoom);
    let pageViewport = page.getViewport({scale: zoom});

    let initialSetup = false;
    let renderTask: Pdfjs.RenderTask | null = null;
    let pageElement: HTMLDivElement;
    let canvas: HTMLCanvasElement | undefined;
    let observer: IntersectionObserver;
    let textContent: TextContent;
    let pageIntersectingWithViewport = false;
    let rendered = false;
    let lastScaleValue: number | undefined;

    $: rendering = exists(renderTask) || !rendered;
    $: if (initialSetup) setupCanvasAndRender(canvas, zoom, !pageIntersectingWithViewport);

    onMount(async () => {
        // First page should get rendered right away
        const initialRenderPage = page.pageNumber === 1;
        setupCanvasAndRender(canvas, zoom, !initialRenderPage);

        // Load text content
        textContent = await page.getTextContent({includeMarkedContent: true, disableNormalization: true});

        // Initialize observer to only re-render visible pages
        observer = new IntersectionObserver((entries) => {
            pageIntersectingWithViewport = entries.some((entry) => entry.isIntersecting);
        }, {
            root: null,
            rootMargin: '100px',
            threshold: 0
        });

        if (exists(pageElement)) {
            observer.observe(pageElement);
        }
    });

    onDestroy(() => {
        // Cancel render task, if it exists
        if (exists(renderTask)) {
            renderTask.cancel();
        }

        if (exists(observer)) {
            observer.disconnect();
        }

        zoomUnsubscribe();
    });

    // Setups canvas and render
    function setupCanvasAndRender(pdfCanvas: HTMLCanvasElement | undefined, currentZoom: number, outsideViewport: boolean = false) {
        if (!exists(pdfCanvas)) {
            return;
        }

        const newPageViewport = page.getViewport({scale: currentZoom});

        // When the canvas was already setup, and it is not in the viewport, don't render nor update it
        if (outsideViewport && initialSetup) {
            return;
        }

        // When the last scale value is the same as the last one, skip this rendering pass
        if (lastScaleValue === newPageViewport.scale && rendered) {
            return;
        }

        pageViewport = newPageViewport;
        setupCanvas(canvas);

        // When the canvas is inside the viewport, render it
        if (!outsideViewport) {
            rendered = false;
            if (exists(renderTask)) {
                renderTask.cancel();
            }

            renderOnCanvas(pdfCanvas).then(() => {
                renderTask = null;
                rendered = true;
                lastScaleValue = pageViewport.scale;
            });
        }

        if (!initialSetup) {
            lastScaleValue = pageViewport.scale;
        }

        initialSetup = true;
    }

    // Setup canvas size and clear it
    function setupCanvas(pdfCanvas: HTMLCanvasElement) {
        const drawContext = pdfCanvas.getContext('2d');
        drawContext.clearRect(0, 0, pdfCanvas.width, pdfCanvas.height);

        pdfCanvas.width = pageViewport.width;
        pdfCanvas.height = pageViewport.height;
    }

    // Starts PDF render task
    async function renderOnCanvas(pdfCanvas: HTMLCanvasElement) {
        const renderContext = {
            canvasContext: pdfCanvas.getContext('2d'),
            viewport: pageViewport
        };

        renderTask = page.render(renderContext);
        return renderTask.promise;
    }

    const handleCanvasContextMenu = (event: Event) => {
        if (!downloadable) {
            event.preventDefault();
        }
    }
</script>

<div id="mv-pdf-page-{page.pageNumber}"
     role="application"
     on:contextmenu={handleCanvasContextMenu}
     bind:this={pageElement}
     class="mv-pdf-page {$currentTheme}"
     style:width="{pageViewport.width}"
     style:height="{pageViewport.height}">

    {#if rendering}
        <div class="rendering-loader-container" transition:fade={renderingFadeAnimParams}>
            <KpLoadingBlock size="sm"/>
        </div>
    {/if}

    <!-- Main PDF layer -->
    <canvas bind:this={canvas} class:not-downloadable={!downloadable}/>

    <!-- Additional layers (annotations and text layer for text selection and copying) Only show them when the page was already rendered first! -->
    {#if initialSetup}
        <MvPdfPageAnnotations {page} {pdfDocument} {module} {pages} viewport="{pageViewport}"/>

        {#if exists(textContent)}
            <MvPdfPageTextLayer {textContent} {module} viewport="{pageViewport}"/>
        {/if}
    {/if}
</div>

<style lang="less">
    @import (reference) "src/features/media-viewer/media-viewer.variables.less";

    .mv-pdf-page {
        isolation: isolate;
        position: relative;
        display: flex;
        border-radius: @border-radius-default;
        overflow: hidden;
        flex-shrink: 0;
        background-color: var(--viewer-bg);
        outline: 1px solid var(--viewer-default-border);

        &.light {
            box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.07);
        }

        canvas.not-downloadable {
            user-select: none;
        }

        .rendering-loader-container {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 1;
        }
    }
</style>